'use client';
import {format} from 'date-fns';
import {cookie} from '@afreecatv/domains';
import globalDomains, {globalAPPScheme} from '@/utils/global-domains';
import {ROUTE} from '@/constants/main/route';
import dayjs from 'dayjs';
import loadScript from 'load-script';
import {isIOS, isMobile} from 'react-device-detect';
import {goLinkWithChannelBridge} from '@/components/channel/common/Bridge';
import {isEmpty} from '@/utils';
import {DISPLAY_THUMBNAIL} from '@/constants/common/thumbnail';

/** Note. 현재 모듈에서 정의한 것을 사용하지 않고 다른 모듈에서 불러오면 순환 참조 문제를 발생시킴 */
const isWebview = (() => {
  if (typeof window === 'undefined') {
    return false;
  }

  let uagent = window.navigator?.userAgent.toLocaleLowerCase();
  return uagent.indexOf('afreeca webview') > -1 ||
    uagent.indexOf('afreeca up webview') > -1 ||
    uagent.indexOf('sooplive webview') > -1 ||
    uagent.indexOf('sooplive up webview') > -1
    ? true
    : false;
})();

/**
 * api호출 시, 캐시방지를 위한 함수
 * @returns {Number}  현재시간 타임스탬프값 리턴
 */
export const getTimeStamp = () => {
  const timestamp = new Date().getTime(); // 현재 타임스탬프
  return timestamp;
};

//시간 분 기준으로 타임스탬프를 반환
export const getOneMinuteTimeStamp = () => {
  const now = new Date();
  const timestamp = Math.floor(now.getTime() / 60000) * 60000;
  return timestamp;
};

//5초 기준으로 타임스탬프를 반환
export const getFiveSecondTimeStamp = () => {
  const now = new Date();
  const timestamp = Math.floor(now.getTime() / 5000) * 5000;
  return timestamp;
};

/**
 * 쿠키 key 로 찾아서 값을 반환한다
 * @param {string} cname - 쿠키 이름
 * @returns 쿠키 값
 */
export const getCookie = (cname) => {
  if (typeof document !== 'undefined') {
    const name = cname + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
  }
  return '';
};

/**
 * 로그인 여부 반환하는 함수
 * @returns {Boolean}  로그인 여부 true/false
 */
export const isLogin = () => {
  return getCookie(cookie.TICKET_COOKIE_NAME) ? true : false;
};

/**
 * 시청자수 1,000,000 형으로 반환
 * @param {Number} number - 성인방송 여부
 * @param {Number} desc - 소수점
 * @returns {String} 변환된 숫자
 */
export const convertViewCnt = (number, desc = 0) => {
  try {
    const num = Number(number);
    const dec = desc; // 소수점 자리수
    const reg = dec ? /(\d)(?=(\d{3})+\.)/g : /(\d)(?=(\d{3})+$)/g;

    return num.toFixed(dec).replace(reg, '$1,'); // 포맷팅된 문자열 반환
  } catch (e) {
    console.error('Error in convertViewCnt:', e);
    return number; // 에러 발생 시 원본 숫자 반환
  }
};

/**
 * 라이브 방송 썸네일 클래스 반환 함수
 * @param {Number} grade - 성인방송 여부
 * @param {String} password - 비번방 여부
 * @returns {String} 클래스
 */
export const getThumbNail = (grade, password) => {
  if (grade === 19 && password === 'Y') {
    return 'thumb-lock_adult';
  } else if (grade === 19 && password === 'N') {
    return 'thumb-adult';
  } else if (grade !== 19 && password === 'Y') {
    return 'thumb-lock';
  } else {
    return '';
  }
};

/**
 * 방송시간 반환 함수
 * @param {String} totalSeconds - 방송시간(초)
 */
export const convertTotalHours = (totalSeconds) => {
  const secondsPerHour = 3600;
  const totalHours = Number(totalSeconds) / secondsPerHour;
  return totalHours;
};

/**
 * 쿠키 set 함수
 * @param {String} name - 쿠키명
 * @param {String} value - 쿠키값
 * @param {String} domain - 도메인
 * @param {Number} expiredays - 만료일
 */
export const setCookie = (
  name,
  value,
  domain = `.${globalDomains.SOOPLIVE_ROOT_DOMAIN}`,
  expiredays = 1,
) => {
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const today = new Date();
    const expiryDate = new Date(
      today.setDate(today.getDate() + parseInt(expiredays)),
    );

    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expiryDate.toGMTString()}; domain=${domain};`;
  }
};

/**
 * 만료시간을 초 단위로 설정할 수 있는 쿠키 설정 함수입니다.
 * @param name - 쿠키명
 * @param value - 쿠키값
 * @param domain - 도메인
 * @param {Number} expireSeconds - 만료 시간 (기본 10초)
 */
export const setCookieWithSeconds = (
  name,
  value,
  domain = `.${globalDomains.SOOPLIVE_ROOT_DOMAIN}`,
  expireSeconds = 10,
) => {
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const now = new Date();
    // 현재 시간에 만료 시간(초)을 더합니다.
    now.setSeconds(now.getSeconds() + expireSeconds);

    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${now.toUTCString()}; domain=${domain};`;
  }
};

/**
 * 쿠키 set 함수
 * @param {String} name - 쿠키명
 * @param {String} value - 쿠키값
 * @param {String} domain - 도메인
 * @param {Number} expireDays - 만료일
 * @param {Boolean} expireAtMidnight - 자정시간 만료 시간 여부
 */
export const setLayerCookie = (
  name,
  value,
  domain = `.${globalDomains.SOOPLIVE_ROOT_DOMAIN}`,
  expireDays = 1,
  expireAtMidnight = false,
) => {
  if (typeof window !== 'undefined') {
    const now = new Date();
    const expiryDate = new Date(now); // 복사

    if (expireAtMidnight) {
      expiryDate.setHours(0, 0, 0, 0); // 자정으로 정확히 설정
    }

    expiryDate.setDate(expiryDate.getDate() + expireDays); //+N일

    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expiryDate.toGMTString()}; domain=${domain};`;
  }
};

/**
 * 주어진 날짜 문자열이 유효한지 확인
 * @param {string} date - 유효성을 검사할 날짜 문자열
 * @returns {boolean} - 날짜가 유효하면 true, 그렇지 않으면 false를 반환
 */
export const isValidDate = (date) => {
  const parsedDate = new Date(date);
  return !isNaN(parsedDate.getTime());
};

/**
 * 날짜 포맷 함수
 * @param {string} date - 날짜
 * @param {string} formatString - 포맷 문자열
 * @param {string} [defaultValue] - date 가 이상할 경우 노출될 기본값
 */
export const formatDate = (date, formatString, defaultValue = '') => {
  if (!date || !formatString || !isValidDate(date)) {
    return defaultValue;
  }

  return format(new Date(date), formatString);
};

/**
 * PdboxUser 쿠키값 가져오는 함수
 * @param {String} key 키값이름
 */
export const getUserCookie = (key) => {
  try {
    let pdBoxUser = decodeURIComponent(getCookie(cookie.USER_COOKIE_NAME));

    for (let val of pdBoxUser.split('&')) {
      if (val.indexOf(key) === 0) {
        return val.substring(key.length + 1);
      }
    }
    return '';
  } catch (e) {
    return '';
  }
};

export const getUserLoginId = () => {
  try {
    const loginId = decodeURIComponent(getCookie(cookie.BBS_COOKIE_NAME));
    if (loginId) {
      return loginId;
    } else {
      return '';
    }
  } catch (error) {
    return '';
  }
};

/**
 * ContextMenu 필터링 리스트 가져오는 함수
 * @param {String[]} contextMenu 리스트메뉴
 * @param {String[]} name 추출할 label 명
 */
export const getFilteredContextMenu = (contextMenu, name = []) => {
  // 로그인 여부 체크해서 비로그인일때 name으로 들어오는 메뉴들만 return
  if (!isLogin()) {
    return contextMenu.filter((item) => name.includes(item.type));
  }
  return contextMenu;
};

/**
 * localStorage get
 * @param {String} key 키값
 * @param {String} defaultValue 디폴트 값
 */
export const getLocalStorage = (key, defaultValue) => {
  try {
    return window.localStorage.getItem(key) === null
      ? defaultValue
      : window.localStorage.getItem(key);
  } catch (e) {
    return defaultValue;
  }
};

/**
 * localStorage set
 * @param {String} key 키값
 * @param {String} value 저장하는 값
 */
export const setLocalStorage = (key = '', value = '') => {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {}
};

/**
 * localStorage remove
 * @param {String} key 키값
 */
export const removeLocalStorage = (key = '') => {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {}
};

/**
 * sessionStorage get
 * @param {String} key 키값
 * @param {String} defaultValue 디폴트 값
 */
export const getSessionStorage = (key, defaultValue) => {
  try {
    return window.sessionStorage.getItem(key) === null
      ? defaultValue
      : window.sessionStorage.getItem(key);
  } catch (e) {
    return defaultValue;
  }
};

/**
 * sessionStorage set
 * @param {String} key 키값
 * @param {String} value 저장하는 값
 */
export const setSessionStorage = (key = '', value = '') => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {}
};

/**
 * 로그인 페이지 URL
 */
export const getLoginUrl = () => {
  const currentUrl = window.encodeURIComponent(window.location.href);
  if (isWebview) {
    return `${globalAPPScheme}login/requireLogin`;
  } else if (isMobile) {
    return `${globalDomains.AUTH_M_AFREECATV}/login?url=${isIOS ? goLinkWithChannelBridge(currentUrl) : currentUrl}`;
  } else {
    return `${globalDomains.LOGIN_AFREECATV}/afreeca/login.php?szFrom=full&request_uri=${currentUrl}`;
  }
};

/**
 * 로그인 페이지
 */
export const goLogin = (blank = false) => {
  if (blank) {
    window.open(getLoginUrl(), '_blank');
  } else {
    location.href = getLoginUrl();
  }
};

export const getLogoutUrl = () => {
  if (isWebview) {
    return `${globalAPPScheme}logout`;
  } else {
    return `${globalDomains.LOGIN_AFREECATV}/app/LogOut.php?request_uri=${window.encodeURIComponent(window.location.href)}`;
  }
};

/**
 * 로그아웃 페이지
 */
export const goLogout = () => {
  location.href = getLogoutUrl();
};

/**
 * 주어진 시간을 시, 분, 초 형식으로 변환
 * @param {number|string} duration - 변환할 초 혹은 "HH:MM:SS", "H:MM:SS", "HH:MM", "MM:SS", "H:MM", "M:SS" 형식의 문자열
 * @returns {string} - "HH:MM:SS" 또는 "H:MM:SS", "MM:SS" 또는 "M:SS" 형식으로 변환된 시간 문자열
 */
export function convertToDuration(duration) {
  if (typeof duration === 'string') {
    const parts = duration.split(':').map((part) => {
      const num = Number(part);
      return isNaN(num) ? null : num;
    });

    if (parts.includes(null)) {
      console.error(
        'duration 문자열 내 모든 부분은 유효한 숫자여야 합니다. 예: "HH:MM:SS", "MM:SS" 등',
      );
      return '';
    }

    if (parts.length === 3) {
      // "HH:MM:SS" 또는 "H:MM:SS" 형식
      const [hours, minutes, seconds] = parts;
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else if (parts.length === 2) {
      // "MM:SS" 또는 "M:SS" 형식
      const [minutes, seconds] = parts;
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    } else {
      console.error(
        'duration 문자열은 "HH:MM:SS", "H:MM:SS", "MM:SS", "M:SS" 형식이어야 합니다.',
      );
      return '';
    }
  }

  // 숫자 형식의 초를 시, 분, 초로 변환
  let totalSeconds = Number(duration);

  if (isNaN(totalSeconds) || totalSeconds < 0) {
    console.error(
      'duration은 음수가 아닌 초 형태의 Number나 "HH:MM:SS", "H:MM:SS", "HH:MM", "MM:SS", "H:MM", "M:SS" 형식의 String이어야 합니다.',
    );
    return '';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  // 항상 시간을 표시하도록 수정 (0 포함)
  const time = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  return time;
}

/**
 * 줄바꿈을 <br> 태그로 변경
 * @param string str
 */
export const newLineToBreak = (str) => {
  return str && str.replace(new RegExp('\r?\n', 'g'), '<br />');
};

/**
 * 메인 배너 노출 횟수 체크 함수 (오전 12시, 오후 12시 주기로 리셋)
 */
export const showMainBanner = (viewLimit) => {
  if (!viewLimit) {
    return false;
  }

  const VIEW_LIMIT = viewLimit;
  const now = dayjs();
  const currentPeriod = now.hour() < 12 ? 'AM' : 'PM';
  const currentDate = now.format('YYYY-MM-DD');

  // 쿠키값 내 도메인을 .sooplive.co.kr 로 맞추어주기위해서 기존에 있던 쿠키값은 날려버림
  const keyList = [
    'lastMainBannerViewCount',
    'lastMainBannerViewDate',
    'lastMainBannerViewPeriod',
  ];
  keyList.forEach((item) => {
    removeCookie(item, globalDomains.WWW_AFREECATV);
  });

  // 쿠키에서 마지막 본 날짜와 시간대, 조회 수를 불러옴
  const lastPeriod = getCookie('lastMainBannerViewPeriod') || '';
  const lastDate = getCookie('lastMainBannerViewDate') || '';
  let lastMainBannerViewCount =
    parseInt(getCookie('lastMainBannerViewCount')) || 0;

  // 날짜 또는 시간대가 달라지면 조회 수 초기화
  if (lastDate !== currentDate || lastPeriod !== currentPeriod) {
    lastMainBannerViewCount = 0;
    setCookie('lastMainBannerViewPeriod', currentPeriod);
    setCookie('lastMainBannerViewDate', currentDate);
  }

  // 조회 수가 제한을 넘지 않는 경우에만 증가 및 노출
  if (lastMainBannerViewCount < VIEW_LIMIT) {
    setCookie('lastMainBannerViewCount', ++lastMainBannerViewCount);
    return true;
  }

  return false;
};

/**
 * route값에 따른 location 값을 반환함
 * @param {string} pathName - 경로
 * @returns {string}
 */
export const filteredLocation = (pathName) => {
  switch (pathName) {
    case ROUTE.INDEX:
      return 'myplus_list';
    default:
      return pathName;
  }
};

/**
 * 주어진 객체를 재귀로 돌아서 값을 뽑아내어 배열로 만듦
 * @param {Object} obj - 값 뽑아낼 객체
 * @returns {(string | number)[]} 객체에서 뽑아낸 값들의 배열
 */
export const getValues = (obj) => {
  const values = [];

  const recursiveHelper = (o) => {
    for (const key in o) {
      if (typeof o[key] === 'object' && o[key] !== null) {
        recursiveHelper(o[key]);
      } else {
        values.push(o[key]);
      }
    }
  };

  recursiveHelper(obj);
  return values;
};

export const jsonToQuerystring = (data) => {
  const queryString = Object.entries(data)
    .map(([key, value]) => {
      return Array.isArray(value)
        ? key + '=' + JSON.stringify(value)
        : key + '=' + value; // 배열은 문자열로
    })
    .filter((v) => v)
    .join('&');
  return queryString;
};

/**
 * Broadcast 아이템의 키를 변환하는 함수
 * @param {Object} content - 키를 변환할 객체
 * @returns {Object} 변환된 키를 가진 객체 (convertedContent)
 */
export const convertContentForBroadcastItem = (content) => {
  // Broadcast 객체의 키 매핑 정의 (TO-BE: AS-IS)
  const keyMapping = {
    accessibleAge: ['grade'],
    allowsAdult: ['isAdult'],
    canResend: ['isVisitBroad'],
    categoryNo: ['category', 'broadCateNo'],
    broadcastId: ['vodNumber', 'broadcastId'],
    originalUserId: ['copyright.originalUserId'],
    originalUserNickname: ['copyright.userNick'],
    thumbnailUrl: ['thumbnailSrc', 'playlist.bbsTitle.ucc.thumb'],
    previewThumbnailUrl: ['webpPath'],
    profileImageUrl: ['adminProfileImg'],
    startDateTime: ['broadcastStartTime'],
    vodType: ['fileType'],
    createdDate: ['registerDate'],
    liveType: ['broadType'],
    updatedDate: ['laterRegDate'],
    playlistId: ['listIdx', 'playlistNumber', 'no', 'listNo'],
    hasDrops: ['isDrops'],
    hasBadge: ['isBadge'],
    hasPassword: ['isPassword'],
    isCinety: ['isOriginalContentaccessibleAge'],
    searchActionType: ['urlActtype'],

    // TODO: 플레이리스트 용도의 함수를 나누든가 해야 할듯
    // 아래는 플레이리스트에서 쓰는 프로퍼티들
    userNickname: ['playlist.regNick'],
    title: ['playlist.title'],
    registerDate: ['playlist.regDate'],
    playlistCount: ['playlist.count.playlistCnt'],
    isDisplay: ['playlist.config.display'],
    playlistLink: ['url', 'playlist.url'],
    thumbnailType: ['playlist.bbsTitle.ucc.thumbType', 'thumbType'], // LOCK / ADULT / ADULT_LOCK / SUCCEED / ''
    registerUserId: ['playlist.regId', 'userId'],
    addUserId: ['playlist.addId'],
    isFan: ['isFan', 'isFanclub'],
  };

  const converted = convertKeysWithMapping(keyMapping, content);

  // 빈 값일 때 덮어 씌우는 것을 방지하기 위해서 필터링을 거친다
  const filteredContent = Object.keys(content).reduce((acc, key) => {
    if (content[key] !== undefined) {
      acc[key] = content[key];
    }
    return acc;
  }, {});

  return {
    ...converted,
    // 겹치는 키의 경우 content의 원래 값을 덮어씌움
    ...filteredContent,
  };
};

/**
 * 키 매핑을 통해 객체의 키를 변환하는 함수
 * @param {Object} keyMapping - TO-BE와 AS-IS 키 매핑 객체
 * @param {Object} content - 키를 변환할 객체
 * @returns {Object} 변환된 키를 가진 객체 (convertedContent)
 */
const convertKeysWithMapping = (keyMapping, content) => {
  const convertedContent = {};

  Object.entries(keyMapping).forEach(([tobeKey, asisKeys]) => {
    for (const asisKey of asisKeys) {
      // 중첩된 객체의 키 처리
      if (asisKey.includes('.')) {
        const keys = asisKey.split('.');
        let value = content;

        // 중첩된 키를 순차적으로 탐색하여 값 찾기
        for (const key of keys) {
          value = value?.[key];
          if (value === undefined) {
            break;
          }
        }

        // 중첩된 키의 값이 존재하면 convertedContent에 추가
        if (value !== undefined) {
          convertedContent[tobeKey] = value;
          break;
        }
      } else {
        // 중첩되지 않은 키의 값이 존재하면 convertedContent에 추가
        if (content[asisKey] !== undefined) {
          convertedContent[tobeKey] = content[asisKey];
          break;
        }
      }
    }
  });

  return convertedContent;
};

const getUiStatusInfoForAU = (szAction, isStation = false) => {
  let uiState = null;

  if (szAction === 'MAIN_LIST' || isStation) {
    uiState = {
      is_lnb_simple:
        getCookie('soop-navigation-ui-config') === 'mini' ? 'Y' : 'N',
    };
  }

  return uiState ? JSON.stringify(uiState) : null;
};

const checkExistingScript = (url) => {
  return Array.from(document.querySelectorAll('script')).find((script) => {
    const urlWithoutQuery = script.src.split('?')[0];
    return urlWithoutQuery === url;
  });
};

/**
 * webuv log 호출하는 함수 _au.php
 * @param {string} [prevPathForReferrer] - 이전 페이지 path 없으면 현재 주소가 지정
 */
export const sendWebUvLog = (prevPathForReferrer) => {
  // TODO : 코드 정리
  const AU_URL = `${globalDomains.ANALYSIS_AFREECATV}/_au.js`;
  const AU_API_URL = `${globalDomains.ANALYSIS_AFREECATV}/_au.php`;

  let page = window.location.pathname.split('/')[1];
  let auAction = [
    'station',
    'search',
    'directory',
    'my',
    'ai',
    'help',
  ].includes(page)
    ? ''
    : 'MAIN_LIST';
  let isStation = page === 'station';

  // commonjs내 callAU()에서 처리하던 작업 동일하게 추가
  window.szLogAction = auAction;
  window.szLayoutData = getUiStatusInfoForAU(auAction, isStation);

  window.referrer = prevPathForReferrer || window.location.href;
  // _au 스크립트를 직접 로드해서 자동 실행하도록 변경
  // 실 QA에서 테스트 시 시 - 실 환경 키바나 webuv에 적재 (실 환경 nowlximgt1 서버에 대한 분기처리 없음)
  loadScript(`${AU_URL}?refresh=${new Date().getTime()}`, () => {
    // 로드한 스크립트 제거
    const existingScript = checkExistingScript(AU_URL);
    const existingApiScript = checkExistingScript(AU_API_URL);

    if (existingScript) {
      existingScript.parentElement.removeChild(existingScript);
    }
    if (existingApiScript) {
      existingApiScript.parentElement.removeChild(existingApiScript);
    }
  });

  // window.callAU && window.callAU(callAUAction);
  //LogApi.callAU(callAUAction);

  // TODO : sendWebUve 함수에서 분리하여 동등한 레벨 함수로 만들고 이를 묶는 함수에서 호출
  window.isCheckedBlock = undefined;
  checkAdvertisementBlocking();
};
/*
* 만약 webuv와 동일한 조건으로 호출해야 한다면
* sendLogsWhenPageMoved라는 함수를 만들고 두 동작을 묶는다.
*
* () => {
sendWebUvLog(prevPathForReferrer);
*
window.isCheckedBlock = undefined;
checkAdvertisementBlocking();
* }
*/
export const checkAdvertisementBlocking = () => {
  // 광고가 블로킹 되었는지 체크한다. 단 이것은 https://res.sooplive.co.kr/script/player/service-maintenance.js 호출 이후 window에 바인딩된 함수를 사용
  // checkBlock 내 페이지 로드 1번에 한번만 checkBlock 하도록 처리되어 있다.
  window.checkBlock && window.checkBlock();
};

/**
 * 배열 셔플
 * 기존 배열 변경 없이 새로운 배열을 변환
 * 피셔 예이츠 알고리즘
 * @param {any[]} sourceArr 기존 배열
 * @param {number} len 셔플할 배열 길이
 * @returns 셔플된 배열
 */

export function shuffleArray(sourceArr, len = 30) {
  const array = sourceArr.concat();
  const arrayLength = array.length;
  const minLen = Math.min(arrayLength, len);
  for (let i = minLen - 1; i >= 0; i--) {
    const randomIndex = Math.floor(Math.random() * (i + 1));
    [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
  }

  return array;
}

/**
 *
 * 쿠키값 제거 함수
 * @param {String} key 키값
 */
export const removeCookie = (
  key,
  domain = `.${globalDomains.SOOPLIVE_ROOT_DOMAIN}`,
) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${key}=; max-age=0; path=/; domain=${domain}`;
};

/**
카테고리 번호가 Number, String 혼용해서 오기때문에 String 8길이로 맞추기 위한 함수
*/
export const categoryNoTo8Digits = (value) => {
  if (isEmpty(value)) {
    return '';
  }
  return String(value).padStart(8, '0');
};

/*
 * 카테고리 썸네일 URL 생성
 *
 * */

export function getCategoryThumbUrl(cateNo, display, type, isDarkMode) {
  if (isEmpty(cateNo)) {
    return null;
  }

  const topCateNo = categoryNoTo8Digits(cateNo).substring(0, 4);
  const imgName = `${topCateNo}_${display}_${isDarkMode ? 'dark' : 'light'}`;
  return `${globalDomains.RES_AFREECATV}/images/thumb/${type}/${imgName}.png`;
}
